import { useState, useEffect, useCallback, useRef, CSSProperties } from 'react'
import { LazyWebGLOrb } from '../shared/LazyWebGLOrb'
import { updateData, getData } from '../../utils/storage'
import { sendMessage, type ExtractedTask } from '../../utils/ai'
import { useTasks } from '../../hooks/useTasks'
import type { OrbState } from '../shared/DynamicOrb'
import type { BurnoutMode, TonePreference } from '../../data/types'

interface OnboardingV2Props {
  onComplete: () => void
}

type Screen = 'breathe' | 'name' | 'energy' | 'tone' | 'mode' | 'concerns' | 'braindump' | 'review' | 'tour' | 'ready'

const SCREENS: Screen[] = ['breathe', 'name', 'energy', 'tone', 'mode', 'concerns', 'braindump', 'review', 'tour', 'ready']

const CONCERN_OPTIONS = [
  'work', 'money', 'health', 'relationships', 'family',
  'school', 'housing', 'self-care', 'creativity', 'purpose'
]

const ENERGY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'running on fumes' },
  { value: 2, label: 'pretty low' },
  { value: 3, label: 'steady' },
  { value: 4, label: 'feeling good' },
  { value: 5, label: 'charged up' }
]

const TONE_OPTIONS: { value: TonePreference; label: string; subtitle: string; demo: string }[] = [
  { value: 'gentle', label: 'gentle', subtitle: 'soft nudges, lots of patience', demo: 'whenever you\'re ready. no rush.' },
  { value: 'direct', label: 'direct', subtitle: 'clear, no sugarcoating', demo: 'you\'ve got this. let\'s move.' },
  { value: 'playful', label: 'playful', subtitle: 'light, a little humor', demo: 'alright, let\'s do the thing.' }
]

const MODE_OPTIONS: { value: BurnoutMode; label: string; subtitle: string }[] = [
  { value: 'recovery', label: 'recovering', subtitle: 'i\'m burned out. go easy on me.' },
  { value: 'prevention', label: 'preventing', subtitle: 'i\'m okay but trying to stay okay.' },
  { value: 'balanced', label: 'balanced', subtitle: 'i\'m good. help me stay productive.' }
]

// Orb scale per screen — hero on breathe/ready, compact on questions, medium on tour
function getOrbScale(screen: Screen): number {
  if (screen === 'breathe' || screen === 'ready') return 1.0
  if (screen === 'tour') return 0.75
  if (screen === 'review') return 0.35
  return 0.5
}

// Orb state based on screen + user selections
function getOrbState(screen: Screen, energy: number, _tone: TonePreference, mode: BurnoutMode, isTyping: boolean, isProcessing: boolean): OrbState {
  if (isProcessing) return 'thinking'
  switch (screen) {
    case 'breathe': return 'idle'
    case 'name': return isTyping ? 'listening' : 'idle'
    case 'energy':
      if (energy <= 2) return 'thinking'
      if (energy >= 4) return 'speaking'
      return 'idle'
    case 'tone': return 'idle'
    case 'mode':
      if (mode === 'recovery') return 'thinking'
      if (mode === 'balanced') return 'notification'
      return 'idle'
    case 'concerns': return 'listening'
    case 'braindump': return isTyping ? 'listening' : 'idle'
    case 'review': return 'notification'
    case 'tour': return 'notification'
    case 'ready': return 'speaking'
    default: return 'idle'
  }
}

// --- Task Review Types ---

interface ReviewTask extends ExtractedTask {
  _id: string        // temp ID for reordering
  _project: string   // detected project name (or "uncategorized")
  _editing: boolean   // is currently being edited inline
}

function groupTasksByProject(tasks: ReviewTask[]): Map<string, ReviewTask[]> {
  const groups = new Map<string, ReviewTask[]>()
  for (const task of tasks) {
    const key = task._project || 'uncategorized'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(task)
  }
  return groups
}

export function OnboardingV2({ onComplete }: OnboardingV2Props) {
  const [screen, setScreen] = useState<Screen>('breathe')
  const [name, setName] = useState('')
  const [energy, setEnergy] = useState(3)
  const [tone, setTone] = useState<TonePreference>('gentle')
  const [burnoutMode, setBurnoutMode] = useState<BurnoutMode>('balanced')
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])
  const [brainDump, setBrainDump] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [tourStep, setTourStep] = useState(0)
  const [isTyping, setIsTyping] = useState(false)
  const [toneKey, setToneKey] = useState(0)
  const [screenKey, setScreenKey] = useState(0)
  const [breatheReady, setBreatheReady] = useState(false)
  const [orbPulse, setOrbPulse] = useState(false)

  // Review screen state
  const [reviewTasks, setReviewTasks] = useState<ReviewTask[]>([])
  const [miloMessage, setMiloMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Drag state
  const dragRef = useRef<{ taskId: string; startY: number; currentY: number } | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)

  const nameInputRef = useRef<HTMLInputElement>(null)
  const brainDumpRef = useRef<HTMLTextAreaElement>(null)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const { addTask } = useTasks()

  // Breathe screen: delay the text reveal
  useEffect(() => {
    if (screen === 'breathe') {
      const t = setTimeout(() => setBreatheReady(true), 800)
      return () => clearTimeout(t)
    }
  }, [screen])

  // Focus inputs when screens appear
  useEffect(() => {
    if (screen === 'name') {
      setTimeout(() => nameInputRef.current?.focus(), 600)
    }
    if (screen === 'braindump') {
      setTimeout(() => brainDumpRef.current?.focus(), 600)
    }
  }, [screen])

  // Track typing state with debounce
  const handleNameChange = useCallback((val: string) => {
    setName(val)
    setIsTyping(true)
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 1000)
  }, [])

  const handleBrainDumpChange = useCallback((val: string) => {
    setBrainDump(val)
    setIsTyping(true)
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 1000)
  }, [])

  // Navigate to next screen with animation
  const goTo = useCallback((target: Screen) => {
    setScreenKey(k => k + 1)
    setScreen(target)
  }, [])

  const goNext = useCallback(() => {
    const idx = SCREENS.indexOf(screen)
    if (idx < SCREENS.length - 1) {
      goTo(SCREENS[idx + 1])
    }
  }, [screen, goTo])

  // Tone selection with brief orb pulse
  const handleToneSelect = useCallback((t: TonePreference) => {
    setTone(t)
    setToneKey(k => k + 1)
    setOrbPulse(true)
    setTimeout(() => setOrbPulse(false), 600)
  }, [])

  // Brain dump processing — sends to AI, extracts tasks + projects, goes to review
  const handleBrainDump = useCallback(async () => {
    if (!brainDump.trim()) {
      goTo('tour')
      return
    }
    setIsProcessing(true)
    try {
      const data = await getData()
      const config = {
        burnoutMode: data.user?.burnoutMode || burnoutMode,
        tonePreference: data.user?.tonePreference || tone,
        userName: name.trim() || data.user?.name || undefined,
        energy: energy
      }

      // Enhanced prompt prepended to user message — instructs Claude to detect projects + extract tasks
      const braindumpInstruction = `BRAINDUMP MODE — Read the user's braindump carefully and:
1. Identify PROJECTS (multi-step efforts with a clear theme) and INDIVIDUAL TASKS.
2. For each project, break it into 2-5 concrete next-action tasks.
3. For standalone tasks, extract them directly.
4. Group tasks by project name using the "suggestedProjectId" field — use a readable project name string like "Milo App" or "Church" or "Mom's Housing".
5. Respond with a SHORT warm message (2-3 sentences max, no bullet points — Milo's voice) acknowledging what they shared.
6. Output ALL tasks in a single \`\`\`tasks JSON block.

IMPORTANT — the "suggestedProjectId" field should be a readable project name (NOT a UUID). Tasks that don't belong to a project should have suggestedProjectId: "general".
Be thorough. Extract EVERYTHING actionable. Don't skip items. If they mention a recurring commitment (church, Bible study), create a task to "set up" that recurring event.

---

`

      const response = await sendMessage(
        [{ role: 'user', content: braindumpInstruction + `Here's everything on my mind right now:\n\n${brainDump}` }],
        config,
        { voiceMode: false }
      )

      setMiloMessage(response.message)

      // Convert extracted tasks to ReviewTasks with project grouping
      if (response.tasks.length > 0) {
        const review: ReviewTask[] = response.tasks.map((task, i) => ({
          ...task,
          _id: `task-${i}-${Date.now()}`,
          _project: task.suggestedProjectId || 'general',
          _editing: false
        }))
        setReviewTasks(review)
        goTo('review')
      } else {
        // No tasks extracted — go to tour
        goTo('tour')
      }
    } catch (err) {
      console.error('Braindump processing failed:', err)
      goTo('tour')
    }
    setIsProcessing(false)
  }, [brainDump, burnoutMode, tone, name, energy, goTo])

  // --- Review screen handlers ---

  const handleRemoveTask = useCallback((taskId: string) => {
    setReviewTasks(prev => prev.filter(t => t._id !== taskId))
  }, [])

  const handleEditTask = useCallback((taskId: string) => {
    setReviewTasks(prev => prev.map(t =>
      t._id === taskId ? { ...t, _editing: true } : { ...t, _editing: false }
    ))
  }, [])

  const handleUpdateTask = useCallback((taskId: string, field: 'verbLabel' | 'taskBody', value: string) => {
    setReviewTasks(prev => prev.map(t =>
      t._id === taskId ? { ...t, [field]: value } : t
    ))
  }, [])

  const handleFinishEdit = useCallback((taskId: string) => {
    setReviewTasks(prev => prev.map(t =>
      t._id === taskId ? { ...t, _editing: false } : t
    ))
  }, [])

  // Drag and drop handlers
  const handleDragStart = useCallback((taskId: string, y: number) => {
    dragRef.current = { taskId, startY: y, currentY: y }
  }, [])

  const handleDragMove = useCallback((y: number) => {
    if (!dragRef.current) return
    dragRef.current.currentY = y
  }, [])

  const handleDragEnd = useCallback(() => {
    if (!dragRef.current || !dragOverId) {
      dragRef.current = null
      setDragOverId(null)
      return
    }

    const { taskId } = dragRef.current
    if (taskId === dragOverId) {
      dragRef.current = null
      setDragOverId(null)
      return
    }

    setReviewTasks(prev => {
      const fromIdx = prev.findIndex(t => t._id === taskId)
      const toIdx = prev.findIndex(t => t._id === dragOverId)
      if (fromIdx === -1 || toIdx === -1) return prev

      const reordered = [...prev]
      const [moved] = reordered.splice(fromIdx, 1)
      reordered.splice(toIdx, 0, moved)
      return reordered
    })

    dragRef.current = null
    setDragOverId(null)
  }, [dragOverId])

  // Save all tasks
  const handleSaveAllTasks = useCallback(async () => {
    if (reviewTasks.length === 0) {
      goTo('tour')
      return
    }

    setIsSaving(true)
    try {
      for (const task of reviewTasks) {
        await addTask({
          verbLabel: task.verbLabel.slice(0, 11),
          taskBody: task.taskBody,
          timeEstimate: task.timeEstimate || 30,
          feedLevel: task.feedLevel || 'medium',
          priority: task.priority || 3,
          source: 'onboarding',
          taskType: task.taskType,
          dueDate: task.dueDate,
          notes: task.notes
        })
      }
      goTo('tour')
    } catch (err) {
      console.error('Failed to save tasks:', err)
      // Still move on even if saving partially fails
      goTo('tour')
    }
    setIsSaving(false)
  }, [reviewTasks, addTask, goTo])

  // Save all onboarding data
  const handleFinish = useCallback(async () => {
    await updateData(data => ({
      ...data,
      user: {
        ...data.user,
        name: name.trim() || undefined,
        burnoutMode,
        tonePreference: tone,
        currentEnergy: energy as 1 | 2 | 3 | 4 | 5
      },
      feedTopics: selectedTopics,
      onboarding: {
        completed: true,
        completedAt: new Date().toISOString(),
        skippedSteps: []
      }
    }))
    localStorage.setItem('milo_gesture_tutorial', 'true')
    onComplete()
  }, [name, burnoutMode, tone, energy, selectedTopics, onComplete])

  // --- Computed values ---
  const orbState: OrbState = orbPulse ? 'speaking' : getOrbState(screen, energy, tone, burnoutMode, isTyping, isProcessing)
  const orbScale = getOrbScale(screen)
  const isHeroScreen = screen === 'breathe' || screen === 'ready'

  // --- Shared styles ---
  const pageStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'var(--bg)',
    overflow: 'hidden',
    zIndex: 100
  }

  const contentStyle: CSSProperties = {
    width: '100%',
    maxWidth: 380,
    padding: '0 var(--space-lg)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-lg)',
    animation: `onboarding-slide-in 450ms cubic-bezier(0.22, 1, 0.36, 1) backwards`
  }

  const titleStyle: CSSProperties = {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-2xl)',
    color: 'var(--text)',
    fontWeight: 400,
    lineHeight: 'var(--line-height-tight)'
  }

  const subtitleStyle: CSSProperties = {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    color: 'var(--text-muted)',
    lineHeight: 'var(--line-height-normal)'
  }

  const nextBtnStyle: CSSProperties = {
    padding: '14px 32px',
    borderRadius: 'var(--radius-full)',
    border: 'none',
    background: 'var(--orb-orange)',
    color: 'white',
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 150ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 150ms ease',
    boxShadow: '0 4px 14px rgba(255, 69, 0, 0.3)',
    WebkitTapHighlightColor: 'transparent'
  }

  const ghostBtnStyle: CSSProperties = {
    padding: '10px 20px',
    borderRadius: 'var(--radius-full)',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-muted)',
    fontSize: 'var(--text-sm)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent'
  }

  // Staggered text helper
  const revealStyle = (delayMs: number): CSSProperties => ({
    animation: `onboarding-text-reveal 500ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms backwards`
  })

  // Card option style
  const cardStyle = (isSelected: boolean): CSSProperties => ({
    width: '100%',
    padding: '16px 20px',
    borderRadius: 'var(--radius-lg)',
    border: isSelected ? '2px solid var(--orb-orange)' : '1.5px solid var(--border)',
    background: isSelected ? 'rgba(255, 69, 0, 0.06)' : 'var(--bg-card)',
    textAlign: 'left' as const,
    cursor: 'pointer',
    transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
    animation: isSelected ? 'selection-pop 300ms cubic-bezier(0.34, 1.56, 0.64, 1)' : undefined,
    WebkitTapHighlightColor: 'transparent'
  })

  // Chip option style
  const chipStyle = (isActive: boolean): CSSProperties => ({
    padding: '10px 18px',
    borderRadius: 'var(--radius-full)',
    border: `1.5px solid ${isActive ? 'var(--orb-orange)' : 'var(--border)'}`,
    background: isActive ? 'var(--orb-orange)' : 'transparent',
    color: isActive ? 'white' : 'var(--text)',
    fontSize: 'var(--text-sm)',
    cursor: 'pointer',
    transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
    fontWeight: isActive ? 600 : 400,
    opacity: isActive ? 1 : 0.75,
    WebkitTapHighlightColor: 'transparent'
  })

  // Energy row style
  const energyRowStyle = (isSelected: boolean): CSSProperties => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderRadius: 'var(--radius-lg)',
    border: isSelected ? '2px solid var(--orb-orange)' : '1.5px solid transparent',
    background: isSelected ? 'rgba(255, 69, 0, 0.06)' : 'transparent',
    cursor: 'pointer',
    transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
    animation: isSelected ? 'selection-pop 300ms cubic-bezier(0.34, 1.56, 0.64, 1)' : undefined,
    WebkitTapHighlightColor: 'transparent'
  })

  // --- Persistent orb layer (doesn't participate in screen slide) ---
  const orbWrapperStyle: CSSProperties = {
    position: isHeroScreen ? 'relative' : 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'transform 800ms cubic-bezier(0.22, 1, 0.36, 1)',
    transform: `scale(${orbScale})`,
    marginTop: isHeroScreen ? 'auto' : 'calc(env(safe-area-inset-top, 0px) + 24px)',
    marginBottom: isHeroScreen ? 'var(--space-xl)' : 'var(--space-md)',
    flexShrink: 0,
    ...(screen === 'breathe' && !breatheReady ? {
      animation: 'onboarding-orb-bloom 1200ms cubic-bezier(0.22, 1, 0.36, 1) 800ms backwards'
    } : {})
  }

  // ========================================
  // SCREEN 1: BREATHE — The Moment
  // ========================================
  if (screen === 'breathe') {
    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={orbWrapperStyle}>
          <LazyWebGLOrb size="full" state={breatheReady ? 'speaking' : 'idle'} />
        </div>

        {breatheReady && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, padding: '0 var(--space-lg)' }}>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-4xl)',
              color: 'var(--text)',
              fontWeight: 400,
              lineHeight: 'var(--line-height-tight)',
              textAlign: 'center',
              ...revealStyle(200)
            }}>
              hey.
            </div>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-4xl)',
              color: 'var(--text)',
              fontWeight: 400,
              lineHeight: 'var(--line-height-tight)',
              textAlign: 'center',
              ...revealStyle(600)
            }}>
              i'm milo.
            </div>
            <div style={{
              ...subtitleStyle,
              textAlign: 'center',
              maxWidth: 280,
              marginTop: 'var(--space-md)',
              ...revealStyle(1200)
            }}>
              i help people who think too much get things done.
            </div>
            <button
              style={{
                ...nextBtnStyle,
                marginTop: 'var(--space-xl)',
                animation: `onboarding-btn-enter 400ms cubic-bezier(0.34, 1.56, 0.64, 1) 2000ms backwards`
              }}
              onClick={() => goTo('name')}
              onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
              onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
            >
              get started
            </button>
          </div>
        )}
      </div>
    )
  }

  // ========================================
  // SCREEN 9: READY — The Handoff
  // ========================================
  if (screen === 'ready') {
    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={{
          ...orbWrapperStyle,
          animation: 'onboarding-orb-bloom 800ms cubic-bezier(0.22, 1, 0.36, 1) backwards'
        }}>
          <LazyWebGLOrb size="full" state="speaking" />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, padding: '0 var(--space-lg)' }}>
          <div style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-3xl)',
            color: 'var(--text)',
            fontWeight: 400,
            textAlign: 'center',
            ...revealStyle(400)
          }}>
            we're good.
          </div>
          <div style={{
            ...subtitleStyle,
            textAlign: 'center',
            marginTop: 'var(--space-sm)',
            ...revealStyle(800)
          }}>
            tap me whenever.
          </div>
          <button
            style={{
              ...nextBtnStyle,
              marginTop: 'var(--space-xl)',
              animation: `onboarding-btn-enter 400ms cubic-bezier(0.34, 1.56, 0.64, 1) 1200ms backwards`
            }}
            onClick={handleFinish}
            onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
            onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          >
            let's go
          </button>
        </div>
      </div>
    )
  }

  // ========================================
  // SCREEN 8: REVIEW — Task Approval
  // ========================================
  if (screen === 'review') {
    const grouped = groupTasksByProject(reviewTasks)
    const projectNames = Array.from(grouped.keys()).sort((a, b) => {
      if (a === 'general') return 1
      if (b === 'general') return -1
      return a.localeCompare(b)
    })

    return (
      <div style={pageStyle}>
        <div style={{ ...orbWrapperStyle, marginTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', marginBottom: 'var(--space-sm)' }}>
          <LazyWebGLOrb size="full" state="notification" />
        </div>

        <div key={screenKey} style={{
          ...contentStyle,
          maxWidth: 420,
          flex: 1,
          overflow: 'auto',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 100px)',
          gap: 'var(--space-md)'
        }}>
          {/* Milo's message */}
          {miloMessage && (
            <div style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
              lineHeight: 'var(--line-height-normal)',
              padding: 'var(--space-sm) 0',
              fontStyle: 'italic',
              ...revealStyle(100)
            }}>
              "{miloMessage}"
            </div>
          )}

          <div style={{ ...titleStyle, fontSize: 'var(--text-xl)', ...revealStyle(200) }}>
            here's what i found.
          </div>
          <div style={{ ...subtitleStyle, ...revealStyle(400), marginTop: -8 }}>
            tap to edit. drag to reorder. remove what doesn't fit.
          </div>

          {/* Task groups by project */}
          {projectNames.map((projectName, gi) => {
            const projectTasks = grouped.get(projectName) || []
            const displayName = projectName === 'general' ? 'other tasks' : projectName

            return (
              <div key={projectName} style={{
                animation: `onboarding-text-reveal 400ms cubic-bezier(0.22, 1, 0.36, 1) ${500 + gi * 100}ms backwards`
              }}>
                {/* Project header */}
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--orb-orange)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  fontWeight: 600,
                  marginBottom: 'var(--space-sm)',
                  paddingLeft: 4
                }}>
                  {displayName}
                  <span style={{ color: 'var(--text-subtle)', fontWeight: 400, marginLeft: 8 }}>
                    {projectTasks.length}
                  </span>
                </div>

                {/* Task cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {projectTasks.map((task) => (
                    <div
                      key={task._id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '12px 14px',
                        borderRadius: 'var(--radius-lg)',
                        background: dragOverId === task._id ? 'rgba(255, 69, 0, 0.08)' : 'var(--bg-card)',
                        border: dragOverId === task._id
                          ? '1.5px solid var(--orb-orange)'
                          : '1px solid var(--border)',
                        transition: 'all 200ms ease',
                        cursor: task._editing ? 'default' : 'pointer',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                      onClick={() => !task._editing && handleEditTask(task._id)}
                      onDragOver={(e) => { e.preventDefault(); setDragOverId(task._id) }}
                      onDragLeave={() => setDragOverId(null)}
                      onDrop={(e) => { e.preventDefault(); handleDragEnd() }}
                      onTouchMove={(e) => {
                        const touch = e.touches[0]
                        handleDragMove(touch.clientY)
                        // Find element under finger
                        const el = document.elementFromPoint(touch.clientX, touch.clientY)
                        const taskCard = el?.closest('[data-task-id]')
                        if (taskCard) {
                          setDragOverId(taskCard.getAttribute('data-task-id'))
                        }
                      }}
                      onTouchEnd={() => handleDragEnd()}
                      data-task-id={task._id}
                    >
                      {/* Drag handle */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          padding: '4px 2px',
                          cursor: 'grab',
                          flexShrink: 0,
                          opacity: 0.4,
                          touchAction: 'none'
                        }}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move'
                          handleDragStart(task._id, e.clientY)
                        }}
                        onTouchStart={(e) => {
                          const touch = e.touches[0]
                          handleDragStart(task._id, touch.clientY)
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div style={{ width: 12, height: 2, background: 'var(--text-muted)', borderRadius: 1 }} />
                        <div style={{ width: 12, height: 2, background: 'var(--text-muted)', borderRadius: 1 }} />
                        <div style={{ width: 12, height: 2, background: 'var(--text-muted)', borderRadius: 1 }} />
                      </div>

                      {/* Task content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {task._editing ? (
                          // Inline edit mode
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                value={task.verbLabel}
                                onChange={e => handleUpdateTask(task._id, 'verbLabel', e.target.value)}
                                style={{
                                  width: 80,
                                  padding: '4px 8px',
                                  border: '1px solid var(--orb-orange)',
                                  borderRadius: 6,
                                  background: 'transparent',
                                  color: 'var(--orb-orange)',
                                  fontSize: 'var(--text-xs)',
                                  fontFamily: 'var(--font-mono)',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  outline: 'none'
                                }}
                                maxLength={11}
                                autoFocus
                              />
                              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-subtle)' }}>
                                {task.timeEstimate}m · {task.feedLevel}
                              </span>
                            </div>
                            <input
                              value={task.taskBody}
                              onChange={e => handleUpdateTask(task._id, 'taskBody', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '4px 0',
                                border: 'none',
                                borderBottom: '1px solid var(--border)',
                                background: 'transparent',
                                color: 'var(--text)',
                                fontSize: 'var(--text-sm)',
                                fontFamily: 'var(--font-body)',
                                outline: 'none'
                              }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                              <button
                                onClick={() => handleFinishEdit(task._id)}
                                style={{
                                  padding: '4px 12px',
                                  borderRadius: 'var(--radius-full)',
                                  border: 'none',
                                  background: 'var(--orb-orange)',
                                  color: 'white',
                                  fontSize: 'var(--text-xs)',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                done
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Display mode
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 10,
                                color: 'var(--orb-orange)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                fontWeight: 600,
                                flexShrink: 0
                              }}>
                                {task.verbLabel}
                              </span>
                              <span style={{
                                fontFamily: 'var(--font-body)',
                                fontSize: 'var(--text-sm)',
                                color: 'var(--text)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {task.taskBody}
                              </span>
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: 8,
                              marginTop: 4,
                              fontSize: 'var(--text-xs)',
                              color: 'var(--text-subtle)'
                            }}>
                              <span>{task.timeEstimate}m</span>
                              <span>·</span>
                              <span>{task.feedLevel}</span>
                              {task.taskType && (
                                <>
                                  <span>·</span>
                                  <span>{task.taskType}</span>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* Remove button */}
                      {!task._editing && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveTask(task._id) }}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--text-subtle)',
                            fontSize: 16,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'color 150ms ease'
                          }}
                          onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                          onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-subtle)')}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          {reviewTasks.length === 0 && (
            <div style={{
              ...subtitleStyle,
              textAlign: 'center',
              padding: 'var(--space-xl) 0'
            }}>
              no tasks to save. let's keep going.
            </div>
          )}
        </div>

        {/* Fixed bottom action bar */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 'var(--space-md) var(--space-lg)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--space-md))',
          background: 'linear-gradient(transparent, var(--bg) 30%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10
        }}>
          <button style={ghostBtnStyle} onClick={() => goTo('tour')}>skip</button>
          <button
            style={{
              ...nextBtnStyle,
              opacity: isSaving ? 0.6 : 1,
              pointerEvents: isSaving ? 'none' : 'auto'
            }}
            onClick={handleSaveAllTasks}
            onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
            onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          >
            {isSaving ? 'saving...' : `save ${reviewTasks.length} task${reviewTasks.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    )
  }

  // ========================================
  // QUESTION SCREENS (2-7, 9-10) — Compact orb + content below
  // ========================================
  return (
    <div style={pageStyle}>
      {/* Orb — persistent, smoothly rescales */}
      <div style={orbWrapperStyle}>
        <LazyWebGLOrb size="full" state={orbState} />
      </div>

      {/* Overall progress dots — question screens only */}
      {(['name', 'energy', 'tone', 'mode', 'concerns', 'braindump'] as Screen[]).includes(screen) && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 'var(--space-md)' }}>
          {['name', 'energy', 'tone', 'mode', 'concerns', 'braindump'].map((s, i) => {
            const currentIdx = ['name', 'energy', 'tone', 'mode', 'concerns', 'braindump'].indexOf(screen)
            const isCurrent = i === currentIdx
            const isCompleted = i < currentIdx
            return (
              <div key={s} style={{
                width: isCurrent ? 20 : 6,
                height: 6,
                borderRadius: 3,
                background: isCurrent || isCompleted ? 'var(--orb-orange)' : 'var(--border)',
                opacity: isCompleted ? 0.4 : 1,
                transition: 'all 300ms cubic-bezier(0.22, 1, 0.36, 1)'
              }} />
            )
          })}
        </div>
      )}

      {/* Content area — slides in on screen change */}
      <div key={screenKey} style={{
        ...contentStyle,
        flex: 1,
        overflow: 'auto',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--space-xl))'
      }}>

        {/* ======== SCREEN 2: NAME ======== */}
        {screen === 'name' && (
          <>
            <div style={{ ...titleStyle, ...revealStyle(100) }}>
              what should i call you?
            </div>
            <input
              ref={nameInputRef}
              value={name}
              onChange={e => handleNameChange(e.target.value)}
              placeholder="your name"
              className="input-field"
              style={{
                width: '100%',
                padding: '12px 0',
                border: 'none',
                borderBottom: '2px solid var(--border)',
                background: 'transparent',
                color: 'var(--text)',
                fontSize: 'var(--text-xl)',
                fontFamily: 'var(--font-display)',
                outline: 'none',
                transition: 'border-color 200ms ease',
                ...revealStyle(300)
              }}
              onFocus={e => (e.currentTarget.style.borderBottomColor = 'var(--orb-orange)')}
              onBlur={e => (e.currentTarget.style.borderBottomColor = 'var(--border)')}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-sm)', ...revealStyle(500) }}>
              <button style={ghostBtnStyle} onClick={goNext}>skip</button>
              {name.trim() && (
                <button
                  style={nextBtnStyle}
                  onClick={goNext}
                  onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
                  onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  next
                </button>
              )}
            </div>
          </>
        )}

        {/* ======== SCREEN 3: ENERGY ======== */}
        {screen === 'energy' && (
          <>
            <div style={{ ...titleStyle, ...revealStyle(100) }}>
              how are you feeling right now?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
              {ENERGY_OPTIONS.map((e, i) => (
                <button
                  key={e.value}
                  onClick={() => setEnergy(e.value)}
                  style={{
                    ...energyRowStyle(energy === e.value),
                    animation: `onboarding-text-reveal 400ms cubic-bezier(0.22, 1, 0.36, 1) ${200 + i * 60}ms backwards`
                  }}
                >
                  <span style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-base)',
                    color: energy === e.value ? 'var(--text)' : 'var(--text-muted)',
                    fontWeight: energy === e.value ? 500 : 400,
                    transition: 'color 200ms ease'
                  }}>
                    {e.label}
                  </span>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: energy === e.value ? 'var(--orb-orange)' : 'var(--border)',
                    transition: 'all 200ms ease',
                    transform: energy === e.value ? 'scale(1)' : 'scale(0.7)'
                  }} />
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', ...revealStyle(600) }}>
              <button
                style={nextBtnStyle}
                onClick={goNext}
                onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
                onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              >
                next
              </button>
            </div>
          </>
        )}

        {/* ======== SCREEN 4: TONE ======== */}
        {screen === 'tone' && (
          <>
            <div style={{ ...titleStyle, ...revealStyle(100) }}>
              when things get hard, i can be...
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              {TONE_OPTIONS.map((t, i) => (
                <button
                  key={t.value}
                  onClick={() => handleToneSelect(t.value)}
                  style={{
                    ...cardStyle(tone === t.value),
                    animation: `onboarding-text-reveal 400ms cubic-bezier(0.22, 1, 0.36, 1) ${200 + i * 80}ms backwards`
                  }}
                >
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)', color: 'var(--text)', fontWeight: 400 }}>
                    {t.label}
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 4 }}>
                    {t.subtitle}
                  </div>
                </button>
              ))}
            </div>

            {/* Live demo line — Milo's voice preview */}
            <div key={toneKey} style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-base)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: 'var(--space-md) 0',
              animation: 'demo-crossfade 300ms cubic-bezier(0.22, 1, 0.36, 1) backwards',
              minHeight: 44
            }}>
              "{TONE_OPTIONS.find(t => t.value === tone)?.demo}"
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', ...revealStyle(600) }}>
              <button
                style={nextBtnStyle}
                onClick={goNext}
                onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
                onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              >
                next
              </button>
            </div>
          </>
        )}

        {/* ======== SCREEN 5: MODE ======== */}
        {screen === 'mode' && (
          <>
            <div style={{ ...titleStyle, ...revealStyle(100) }}>
              where are you at right now?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              {MODE_OPTIONS.map((m, i) => (
                <button
                  key={m.value}
                  onClick={() => setBurnoutMode(m.value)}
                  style={{
                    ...cardStyle(burnoutMode === m.value),
                    animation: `onboarding-text-reveal 400ms cubic-bezier(0.22, 1, 0.36, 1) ${200 + i * 80}ms backwards`
                  }}
                >
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)', color: 'var(--text)', fontWeight: 400 }}>
                    {m.label}
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 4 }}>
                    {m.subtitle}
                  </div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', ...revealStyle(600) }}>
              <button
                style={nextBtnStyle}
                onClick={goNext}
                onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
                onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              >
                next
              </button>
            </div>
          </>
        )}

        {/* ======== SCREEN 6: CONCERNS ======== */}
        {screen === 'concerns' && (
          <>
            <div style={{ ...titleStyle, ...revealStyle(100) }}>
              what areas feel heavy right now?
            </div>
            <div style={{ ...subtitleStyle, ...revealStyle(300) }}>
              pick as many as you want. or none.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
              {CONCERN_OPTIONS.map((topic, i) => (
                <button
                  key={topic}
                  onClick={() => setSelectedTopics(prev =>
                    prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]
                  )}
                  style={{
                    ...chipStyle(selectedTopics.includes(topic)),
                    animation: `onboarding-text-reveal 350ms cubic-bezier(0.22, 1, 0.36, 1) ${400 + i * 50}ms backwards`
                  }}
                >
                  {topic}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...revealStyle(900) }}>
              <button style={ghostBtnStyle} onClick={goNext}>skip</button>
              <button
                style={nextBtnStyle}
                onClick={goNext}
                onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
                onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              >
                {selectedTopics.length > 0 ? 'next' : 'skip'}
              </button>
            </div>
          </>
        )}

        {/* ======== SCREEN 7: BRAINDUMP ======== */}
        {screen === 'braindump' && (
          <>
            <div style={{ ...titleStyle, ...revealStyle(100) }}>
              okay. dump everything on your mind.
            </div>
            <div style={{ ...subtitleStyle, ...revealStyle(400) }}>
              tasks, worries, random thoughts. i'll sort it out.
            </div>
            <textarea
              ref={brainDumpRef}
              value={brainDump}
              onChange={e => handleBrainDumpChange(e.target.value)}
              placeholder="what's been on your mind..."
              style={{
                width: '100%',
                minHeight: 160,
                padding: 'var(--space-md) 0',
                borderRadius: 0,
                border: 'none',
                borderBottom: '1px solid var(--border)',
                background: 'transparent',
                color: 'var(--text)',
                fontSize: 'var(--text-base)',
                fontFamily: 'var(--font-body)',
                lineHeight: '1.8',
                resize: 'none',
                outline: 'none',
                ...revealStyle(600)
              }}
            />

            {isProcessing ? (
              <div style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                textAlign: 'center',
                padding: 'var(--space-md) 0'
              }}>
                sorting through your thoughts
                <span style={{ animation: 'demo-crossfade 1.5s ease-in-out infinite' }}>...</span>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...revealStyle(800) }}>
                <button style={ghostBtnStyle} onClick={() => goTo('tour')}>skip</button>
                <button
                  style={nextBtnStyle}
                  onClick={handleBrainDump}
                  onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
                  onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  {brainDump.trim() ? 'sort it out' : 'skip'}
                </button>
              </div>
            )}
          </>
        )}

        {/* ======== SCREEN 9: TOUR ======== */}
        {screen === 'tour' && (
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-lg)', paddingTop: 'var(--space-md)', flex: 1 }}
            onClick={() => {
              if (tourStep < 2) {
                setTourStep(s => s + 1)
              } else {
                goTo('ready')
              }
            }}
          >
            {/* Gesture hint area */}
            <div style={{ position: 'relative', width: 120, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {tourStep === 0 && (
                /* Tap ring */
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  border: '2px solid var(--orb-orange)',
                  animation: 'gesture-tap-ring 1.5s ease-in-out infinite'
                }} />
              )}
              {tourStep === 1 && (
                /* Swipe right dot */
                <div style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: 'var(--orb-orange)',
                  animation: 'gesture-dot-right 1.8s ease-in-out infinite',
                  boxShadow: '0 0 12px rgba(255, 69, 0, 0.4)'
                }} />
              )}
              {tourStep === 2 && (
                /* Swipe left dot */
                <div style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: 'var(--orb-purple, #7C3AED)',
                  animation: 'gesture-dot-left 1.8s ease-in-out infinite',
                  boxShadow: '0 0 12px rgba(124, 58, 237, 0.4)'
                }} />
              )}
            </div>

            {/* Tour text */}
            <div key={tourStep} style={{ textAlign: 'center', animation: 'onboarding-text-reveal 400ms cubic-bezier(0.22, 1, 0.36, 1) backwards' }}>
              <div style={{ ...titleStyle, fontSize: 'var(--text-xl)', textAlign: 'center' }}>
                {tourStep === 0 && 'tap me to talk'}
                {tourStep === 1 && 'swipe right for tasks'}
                {tourStep === 2 && 'swipe left for wellness'}
              </div>
              <div style={{ ...subtitleStyle, textAlign: 'center', marginTop: 'var(--space-xs)' }}>
                {tourStep === 0 && 'voice or text. your choice.'}
                {tourStep === 1 && 'your organized to-do list.'}
                {tourStep === 2 && 'health, check-ins, supplements.'}
              </div>
            </div>

            {/* Progress dots */}
            <div style={{ display: 'flex', gap: 10 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: i === tourStep ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === tourStep ? 'var(--orb-orange)' : 'var(--border)',
                  transition: 'all 300ms cubic-bezier(0.22, 1, 0.36, 1)'
                }} />
              ))}
            </div>

            {/* Bottom hint / button */}
            {tourStep < 2 ? (
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-subtle)',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.1em',
                marginTop: 'auto',
                paddingBottom: 'var(--space-lg)'
              }}>
                tap to continue
              </div>
            ) : (
              <button
                style={{ ...nextBtnStyle, marginTop: 'auto', marginBottom: 'var(--space-lg)' }}
                onClick={(e) => { e.stopPropagation(); goTo('ready') }}
                onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
                onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              >
                got it
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
